import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

const TOUR_COMPLETED_KEY = "otl_board_tour_completed";
export const TOUR_TOTAL_STEPS = 14;

interface TourContextValue {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  startTour: () => void;
  restartTour: () => void;
  nextStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
}

export const TourContext = createContext<TourContextValue | null>(null);

export const TourProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const startTour = useCallback(() => {
    if (localStorage.getItem(TOUR_COMPLETED_KEY)) return;
    setCurrentStep(1);
    setIsActive(true);
  }, []);

  const restartTour = useCallback(() => {
    localStorage.removeItem(TOUR_COMPLETED_KEY);
    setCurrentStep(1);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((s) => {
      if (s >= TOUR_TOTAL_STEPS) return s;
      return s + 1;
    });
  }, []);

  const skipTour = useCallback(() => {
    localStorage.setItem(TOUR_COMPLETED_KEY, "true");
    setIsActive(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_COMPLETED_KEY, "true");
    setIsActive(false);
  }, []);

  return (
    <TourContext.Provider
      value={{
        isActive,
        currentStep,
        totalSteps: TOUR_TOTAL_STEPS,
        startTour,
        restartTour,
        nextStep,
        skipTour,
        completeTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextValue => {
  const ctx = useContext(TourContext);
  if (ctx === null) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return ctx;
};
